import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authQueries } from 'src/api/queries';
import { getRequestOutcomesQueryKey } from 'src/features/requests/request-outcome/use-request-outcomes-query-context';

interface InvalidateRequestQueriesOptions {
  requestId: string;
  companyId?: string;
  editorId?: string;
  isOutcomeFlow?: boolean;
  parentId?: string;
}

/**
 * Hook that provides a function to invalidate all request-related queries.
 * Uses a broader invalidation pattern to ensure consistent updates across all tabs/views.
 * This pattern matches how other successful mutations work (e.g., use-change-request-status-mutation.tsx).
 */
const useInvalidateRequestQueries = () => {
  const client = useQueryClient();

  const invalidateRequestQueries = useCallback(
    (options: InvalidateRequestQueriesOptions) => {
      const { requestId, companyId, editorId, isOutcomeFlow, parentId } = options;

      // Invalidate individual request
      client.invalidateQueries({
        queryKey: [authQueries.request, requestId],
      });

      // Invalidate ALL requests queries to ensure consistent updates across all tabs/views
      client.invalidateQueries({
        queryKey: [authQueries.requests],
      });

      // Invalidate company requests
      if (companyId) {
        client.invalidateQueries({
          queryKey: [authQueries.company, companyId],
        });
      }

      // Invalidate user/editor requests
      if (editorId) {
        client.invalidateQueries({
          queryKey: [authQueries.user, editorId],
        });
      }

      // Invalidate outcome queries for outcome flow requests
      if (isOutcomeFlow && parentId) {
        client.invalidateQueries({
          queryKey: getRequestOutcomesQueryKey(parentId, {}),
        });
      }
    },
    [client],
  );

  return { invalidateRequestQueries };
};

export { useInvalidateRequestQueries };
export type { InvalidateRequestQueriesOptions };
